import * as yup from 'yup'

import { COMMISSION_QUESTIONS_FORM_FIELDS } from '@/components/misc/ReferralFlow/CommissionQuestions/constants'
import { CommissionQuestionsFormValues } from '@/components/misc/ReferralFlow/CommissionQuestions/types'
import {
  ERROR_MESSAGES,
  EXPECTED_COMMISSION_AMOUNT_UNSURE,
  REFERRAL_TYPE,
  REFERRAL_TYPE_OPTION,
} from '@/lib/constants'
import {
  arrayToCommaSeparatedString,
  getFirstAndLastName,
  startsWithVowel,
} from '@/lib/helpers/helperFunctions'
import { yupSchemas } from '@/lib/helpers/yupSchemas'
import clientService from '@/lib/services/clientService'
import companyTypeService from '@/lib/services/companyTypeService'
import userService from '@/lib/services/userService'
import { CLIENT_DETAILS_FORM_FIELDS } from '@/pages/referClient/steps/1_ClientDetails/constants'
import { REFERRAL_JUSTIFICATIONS_QUESTIONS } from '@/pages/referClient/steps/2_ReferralDetails/components/ReferralJustificationQuestion/constants'
import { getToCompanyContactName } from '@/pages/referClient/steps/4a_ConfirmReferralToExistingAdviser/helpers'
import { ClientEntity } from '@/types/entities'
import { POOL_ROUTING_MODE } from '@/types/misc'
import {
  ClientPartyType,
  CompanyTypeCode,
  NormalReferralTypeOption,
  REFERRAL_CONSENT_TYPE,
  ReferralConsentType,
  ReferralIndividual,
  ReferralType,
  ReferralTypeOption,
} from '@/types/misc'
import { CompanyTypeItem, ServiceAreaItem } from '@/types/responses/common-data'
import { CompassReportCollectionItem } from '@/types/responses/compass-reports'
import { ExistingAdviserReferralMetadataItem } from '@/types/responses/referral-metadata'
import {
  ReferralEmailItem,
  ReferralIndividualCollectionItem,
} from '@/types/responses/referrals'

interface OptionDetail {
  label: string
  description: string
}

/**
 * Pill/badge label shown on advisers who are disabled because a routing rule
 * excludes them from the sender's eligible pool.
 */
export const BLOCKED_BY_ROUTING_RULE_BADGE_LABEL = 'Assigned internally'

export interface RoutingRulePickerState {
  /** Hide the individuals picker entirely: the rule decides the recipients. */
  hidePicker: boolean
  /**
   * When set, only these users (the rule's pool) are selectable in the
   * picker. Everyone else still appears but is disabled with a
   * "Not available" pill.
   */
  eligiblePoolUserIds: number[] | undefined
}

class ReferralFlowService {
  isNormalReferralType(
    referralTypeOption?: ReferralTypeOption,
  ): referralTypeOption is NormalReferralTypeOption {
    return (
      !!referralTypeOption &&
      referralTypeOption !== REFERRAL_TYPE_OPTION.bookCallForClient
    )
  }

  hasClientConsent(consentType?: ReferralConsentType) {
    return !this.needsClientConsent(consentType)
  }

  needsClientConsent(consentType?: ReferralConsentType) {
    return consentType === REFERRAL_CONSENT_TYPE.yesESignature
  }

  getConsentType(
    metadata?: ExistingAdviserReferralMetadataItem,
  ): ReferralConsentType | undefined {
    return metadata?.shouldUseESignatureOption
      ? REFERRAL_CONSENT_TYPE.yesESignature
      : undefined
  }

  shouldCcClientOnInitialEmail(referralTypeOption?: ReferralTypeOption) {
    return referralTypeOption === REFERRAL_TYPE_OPTION.directCc
  }

  getReferralTypeOptionDetails(
    referralTypeOption: ReferralTypeOption,
  ): OptionDetail {
    const labels: Record<ReferralTypeOption, OptionDetail> = {
      directCc: {
        label:
          'Directly introduce your client to the adviser(s), CC your client',
        description:
          'Send the professional your client’s details, with your client on copy.',
      },
      direct: {
        label: 'Directly introduce your client to the adviser(s)',
        description:
          'Send the professional your client’s details so they can contact them.',
      },
      directPco: {
        label: 'Directly introduce your client to the adviser(s)',
        description:
          'Send the professional your client’s details so they can contact them.',
      },
      indirect: {
        label: 'Share details of the firm with your client',
        description:
          'Send your client the details of the professional for them to schedule a call.',
      },
      bookCallForClient: {
        label: 'Book a call for your client',
        description:
          "Book a call directly in the professional's diary on your client's behalf.",
      },
      affiliateLink: {
        label: 'Send your client a link',
        description:
          'Send your client a link to sign up for the professional’s service.',
      },
    }

    const label = labels[referralTypeOption]

    if (!label) {
      throw new Error(`Unknown referral type option: ${referralTypeOption}`)
    }

    return label
  }

  normalizeReferralTypeOrFail(referralType?: ReferralTypeOption): ReferralType {
    const normalizedReferralType = this.normalizeReferralType(referralType)

    if (!normalizedReferralType) {
      throw new Error('Expected normalizedReferralType to be set')
    }

    return normalizedReferralType
  }

  normalizeReferralType(
    referralType?: ReferralTypeOption,
  ): ReferralType | undefined {
    if (!referralType) {
      return undefined
    }

    if (
      referralType === REFERRAL_TYPE_OPTION.directCc ||
      referralType === REFERRAL_TYPE_OPTION.direct ||
      referralType === REFERRAL_TYPE_OPTION.bookCallForClient ||
      referralType === REFERRAL_TYPE_OPTION.directPco
    ) {
      return REFERRAL_TYPE.direct
    }

    if (referralType === REFERRAL_TYPE_OPTION.affiliateLink) {
      return REFERRAL_TYPE.indirect
    }

    return referralType as ReferralType
  }

  canUserEditEmailMessage({
    metadata,
    referralType,
  }: {
    metadata?: ExistingAdviserReferralMetadataItem
    referralType?: ReferralTypeOption
  }) {
    if (referralType === REFERRAL_TYPE_OPTION.affiliateLink) {
      return false
    }

    const isClientConsentRequiredForIndirectReferral =
      referralType === REFERRAL_TYPE_OPTION.indirect &&
      !!metadata?.shouldUseESignatureOption

    // If the client's consent is required for an indirect referral, we will
    // send a pre-configured consent email to the client that shouldn't be
    // editable by the referral sender
    if (isClientConsentRequiredForIndirectReferral) {
      return false
    }

    // In all other cases, allow editing the email message
    return true
  }

  // TODO: Move this logic to the API
  buildUserEmailMessage(context: UserEmailMessageContext): string {
    const {
      clientDetails,
      reason,
      referralType,
      toCompanyPresentationName,
      toCompanyContactNames,
      selectedServiceAreas,
      compassReport,
      companyTypes,
      companyTypeCode,
    } = context

    if (
      referralType === REFERRAL_TYPE_OPTION.affiliateLink &&
      context.affiliateLinkEmailIntro
    ) {
      return context.affiliateLinkEmailIntro
    }

    const fullName =
      clientDetails?.fullName || '<Client-first-name> <Client-last-name>'
    const { firstName } = getFirstAndLastName(fullName)
    const abbreviatedNameAndPartyName =
      clientService.getAbbreviatedNameAndPartyName({
        fullName,
        partyName: clientDetails.partyName,
      })
    const nameAndPartyName = clientDetails.partyName
      ? `${fullName} (${clientDetails.partyName})`
      : fullName

    const toCompanyContactName = getToCompanyContactName(toCompanyContactNames)
    const serviceAreaNames = selectedServiceAreas.map(
      (serviceArea) => serviceArea.name,
    )
    const serviceAreaNamesString = arrayToCommaSeparatedString(serviceAreaNames)
    const compassReportMessage = compassReport
      ? ` Please find background information on ${firstName}'s personal affairs in the attached snapshot. `
      : ' '

    const readableCompanyType = companyTypeService.getCompanyTypeByCodeOrFail(
      companyTypes,
      companyTypeCode,
    )

    const messages: Partial<Record<ReferralTypeOption, string[]>> = {
      direct: [
        `Dear ${toCompanyContactName},\n\n`,
        `Our client, ${abbreviatedNameAndPartyName} is in need of advice regarding ${serviceAreaNamesString}.${compassReportMessage}Are you able to please reach out to them on the below number / email address?`,
        reason?.length ? `\n\n${reason}` : '',
      ],
      directPco: [
        `Dear ${firstName},\n\n`,
        'In order to get a Will organised, we have passed your details to Private Client Online. You will hear from them shortly.',
      ],
      directCc: [
        `Dear ${toCompanyContactName},\n\n`,
        `${nameAndPartyName} (cc'd) is after some advice on ${serviceAreaNamesString}.${compassReportMessage}Can I leave it with you to please reach out to ${firstName}?`,
        reason?.length ? `\n\n${reason}` : '',
      ],
      indirect: [
        `Dear ${firstName},\n\n`,
        `You have requested advice concerning ${serviceAreaNamesString}. Please see below for details of ${
          startsWithVowel(readableCompanyType.name) ? 'an' : 'a'
        } ${
          readableCompanyType.name
        } that I think will be able to assist. Feel free to schedule a call with them using the below 'Schedule a conversation' link.`,
      ],
      affiliateLink: [
        `Dear ${firstName},\n\n`,
        `As discussed, here's the link to get started with ${toCompanyPresentationName}.`,
      ],
    }

    const message = messages[referralType]

    if (!message) {
      throw new Error(`No message found for referral type: ${referralType}`)
    }

    return message.join('')
  }

  getClientDetailsValidationSchema() {
    return {
      [CLIENT_DETAILS_FORM_FIELDS.firstName]: yup
        .string()
        .required("The client's first name is required"),

      [CLIENT_DETAILS_FORM_FIELDS.lastName]: yup
        .string()
        .required("The client's last name is required"),

      [CLIENT_DETAILS_FORM_FIELDS.email]: yup
        .string()
        .email('Must be a valid email')
        .required("The client's email is required"),

      [CLIENT_DETAILS_FORM_FIELDS.dateOfBirth]: yupSchemas.dayMonthYear(),
    }
  }

  getReferralJustificationValidationSchema(options: {
    existingAdviserReferralMetadata?: ExistingAdviserReferralMetadataItem
  }) {
    const { existingAdviserReferralMetadata } = options

    return {
      [REFERRAL_JUSTIFICATIONS_QUESTIONS.referralJustification]: yup
        .string()
        .when('_', (_, schema) => {
          if (
            existingAdviserReferralMetadata?.shouldJustifyReasonForIntroduction
          ) {
            return schema.required()
          }

          return schema
        }),
    }
  }

  getCommissionQuestionsValidationSchema(options: {
    existingAdviserReferralMetadata?: ExistingAdviserReferralMetadataItem
  }) {
    const { existingAdviserReferralMetadata } = options

    return {
      [COMMISSION_QUESTIONS_FORM_FIELDS.isExpectingCommission]: yup
        .boolean()
        .test(
          'is-expecting-commission-question,',
          ERROR_MESSAGES.required,
          (value: unknown) => {
            if (existingAdviserReferralMetadata?.shouldAskCommissionQuestions) {
              return typeof value === 'boolean'
            }

            return true
          },
        ),

      [COMMISSION_QUESTIONS_FORM_FIELDS.knowsCommissionAmount]: yup
        .boolean()
        .test(
          'knows-commission-amount',
          ERROR_MESSAGES.required,
          (value, context) => {
            const formValues = context.parent as CommissionQuestionsFormValues

            if (formValues.isExpectingCommission) {
              return typeof value === 'boolean'
            }

            return true
          },
        ),

      [COMMISSION_QUESTIONS_FORM_FIELDS.expectedCommissionAmount]: yup
        .mixed()
        .test(
          'expected-commission-amount',
          ERROR_MESSAGES.required,
          (_value, context) => {
            const formValues = context.parent as CommissionQuestionsFormValues

            if (
              formValues.isExpectingCommission &&
              formValues.knowsCommissionAmount
            ) {
              return !!formValues.expectedCommissionAmount
            }

            return true
          },
        )
        .test(
          'is-valid-money-amount-or-negative-one',
          'Must be a valid money amount or Unsure',
          (value) => {
            const validMoneySchema = yupSchemas.validMoneyValue()

            return (
              validMoneySchema.isValidSync(value) ||
              value === EXPECTED_COMMISSION_AMOUNT_UNSURE
            )
          },
        ),
    }
  }

  getExpectedCommissionAmount({
    isExpectingCommission,
    expectedCommissionAmount,
    knowsCommissionAmount,
  }: {
    isExpectingCommission?: boolean
    expectedCommissionAmount?: number
    knowsCommissionAmount?: boolean
  }) {
    return isExpectingCommission &&
      expectedCommissionAmount &&
      knowsCommissionAmount
      ? expectedCommissionAmount
      : EXPECTED_COMMISSION_AMOUNT_UNSURE
  }

  makeReferralIndividual(
    individual: ReferralIndividualCollectionItem,
  ): ReferralIndividual {
    return {
      companyUserId: individual.companyUser.id,
      userId: individual.user.id,
      fullName: individual.user.fullName,
      company: individual.company,
    }
  }

  isDirectReferral(referralType?: ReferralTypeOption) {
    return (
      referralType === REFERRAL_TYPE_OPTION.direct ||
      referralType === REFERRAL_TYPE_OPTION.directCc
    )
  }

  /**
   * How the routing rule (if any) affects the "choose individuals" picker in
   * the referral journey:
   *
   * - A non-overridable rule hides the picker entirely — the rule decides the
   *   recipients.
   * - A SENDER_SELECTS rule shows the picker with every adviser visible, but
   *   only the rule's eligible adviser pool stays selectable
   *   (`selectedRoutingRuleUsers` contains the full pool for such rules);
   *   everyone else shows a "Not available" pill and is disabled. Note
   *   `isOverridableBySender` is false on SENDER_SELECTS rules, so this case
   *   must be checked first.
   */
  getRoutingRulePickerState(
    metadata: ExistingAdviserReferralMetadataItem | undefined,
  ): RoutingRulePickerState {
    const routingRule = metadata?.referralRoutingRule

    if (routingRule?.poolRoutingMode === POOL_ROUTING_MODE.senderSelects) {
      const poolUserIds = (metadata?.selectedRoutingRuleUsers ?? []).map(
        (user) => user.id,
      )

      return {
        hidePicker: false,
        eligiblePoolUserIds: poolUserIds.length > 0 ? poolUserIds : undefined,
      }
    }

    return {
      hidePicker: !metadata || routingRule?.isOverridableBySender === false,
      eligiblePoolUserIds: undefined,
    }
  }

  /**
   * Tooltip/error copy explaining why an adviser shows the
   * `BLOCKED_BY_ROUTING_RULE_BADGE_LABEL` pill and can't be selected.
   */
  getBlockedByRoutingRuleMessage(companyName: string): string {
    return `${companyName} manages referral routing. You can select from the available advisers or skip this step and the firm will assign it to the appropriate person.`
  }

  getEffectiveToCompanyUserIris(
    metadata: ExistingAdviserReferralMetadataItem | undefined,
    individuals: Array<Pick<ReferralIndividual, 'userId'>>,
  ): string[] {
    const routingRule = metadata?.referralRoutingRule
    const ruleUsers = metadata?.selectedRoutingRuleUsers ?? []
    const pickedIris = individuals.map((individual) =>
      userService.getIri(individual.userId),
    )

    if (!routingRule || ruleUsers.length === 0) {
      return pickedIris
    }

    const ruleUserIris = ruleUsers.map((user) => user['@id'])

    if (routingRule.poolRoutingMode === POOL_ROUTING_MODE.senderSelects) {
      const poolUserIds = ruleUsers.map((user) => user.id)
      const inPoolPickIris = individuals
        .filter((individual) => poolUserIds.includes(individual.userId))
        .map((individual) => userService.getIri(individual.userId))

      return inPoolPickIris.length > 0 ? inPoolPickIris : ruleUserIris
    }

    if (routingRule.isOverridableBySender) {
      return pickedIris.length > 0 ? pickedIris : ruleUserIris
    }

    return ruleUserIris
  }

  buildRoutingRuleWarning(
    metadata: ExistingAdviserReferralMetadataItem | undefined,
    individuals: ReferralIndividual[],
  ): string | undefined {
    if (
      !metadata?.referralRoutingRule ||
      metadata.referralRoutingRule.isOverridableBySender ||
      metadata.selectedRoutingRuleUsers.length === 0 ||
      individuals.length === 0
    )
      return undefined

    // Under SENDER_SELECTS the sender's in-pool picks are honoured, so only
    // warn when some picks fall outside the pool (e.g. restored from a stale
    // link or the adviser was removed from the pool mid-journey). The backend
    // keeps the in-pool picks, or assigns the whole pool if none are valid.
    if (
      metadata.referralRoutingRule.poolRoutingMode ===
      POOL_ROUTING_MODE.senderSelects
    ) {
      const poolUserIds = metadata.selectedRoutingRuleUsers.map(
        (user) => user.id,
      )
      const inPoolPicks = individuals.filter((individual) =>
        poolUserIds.includes(individual.userId),
      )

      if (inPoolPicks.length === individuals.length) return undefined

      const recipientNames =
        inPoolPicks.length > 0
          ? inPoolPicks.map((individual) => individual.fullName)
          : metadata.selectedRoutingRuleUsers.map((user) => user.fullName)

      return `This firm has routing rules enforced. Some of your selected advisers are not eligible, so your referral will be sent to ${buildRecipientsSummary(recipientNames)}.`
    }

    const routingUserIris = metadata.selectedRoutingRuleUsers.map(
      (u) => u['@id'],
    )
    const isExactMatch =
      individuals.length === metadata.selectedRoutingRuleUsers.length &&
      individuals.every((ind) =>
        routingUserIris.some((iri) => iri.endsWith(`/${ind.userId}`)),
      )
    if (isExactMatch) return undefined

    return `This firm has routing rules enforced. Your referral will be sent to ${buildRecipientsSummary(metadata.selectedRoutingRuleUsers.map((user) => user.fullName))}.`
  }
}

/** Formats recipient names as "Alice", "Alice and 1 other", "Alice and 2 others". */
function buildRecipientsSummary(recipientNames: string[]): string {
  const othersCount = recipientNames.length - 1
  const suffix =
    othersCount > 0
      ? ` and ${othersCount} ${othersCount === 1 ? 'other' : 'others'}`
      : ''

  return `${recipientNames[0]}${suffix}`
}

const referralFlowService = new ReferralFlowService()

export default referralFlowService

export interface UserEmailMessageContext {
  clientDetails: Pick<ClientEntity, 'fullName'> & {
    partyType?: ClientPartyType
    partyName?: string
  }
  reason?: string
  referralType: ReferralTypeOption
  toCompanyContactNames: ReferralEmailItem['toNames']
  selectedServiceAreas: ServiceAreaItem[]
  compassReport?: CompassReportCollectionItem
  companyTypes: CompanyTypeItem[]
  companyTypeCode: CompanyTypeCode
  toCompanyPresentationName: string
  affiliateLinkEmailIntro: string | null
  existingAdviserReferralMetadata?: ExistingAdviserReferralMetadataItem
}
